import { initializeApp } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { getFirestore, collection, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import * as PRODUCT from "./../constants/ProductConst";

//  web app's Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// App check
if (process.env.NODE_ENV === "development" || window.location.hostname === "localhost") {
  window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}
/*
const appCheck = initializeAppCheck(app, {
  provider: new ReCaptchaV3Provider(
    process.env.REACT_APP_FIREBASE_APPCHECK_SITE
  ),
  isTokenAutoRefreshEnabled: true,
});
*/

export const firebaseAuth = getAuth(app);
// Set persistence to local storage (works across subdomains)
setPersistence(firebaseAuth, browserLocalPersistence).catch((error) => {
  console.error("Error setting auth persistence:", error);
});

export const firebaseDB = getFirestore(app);
export const firebaseStorage = getStorage(app);
export const firebaseFunctions = getFunctions(app, process.env.REACT_APP_FIREBASE_SERVER_LOCATION);
export default app;

/* Emulator setup */
// Check for emulator mode via environment variable or localhost
const shouldUseEmulator =
  process.env.REACT_APP_USE_EMULATOR === "true" || window.location.hostname === "localhost";

// Ports are overridable so an isolated per-worktree emulator suite (offset
// ports) can be targeted; unset vars keep the standard ports. CRA inlines
// REACT_APP_* at build time, so these must be set when the dev server starts.
const emulatorPorts = {
  auth: Number(process.env.REACT_APP_EMULATOR_AUTH_PORT || 9099),
  firestore: Number(process.env.REACT_APP_EMULATOR_FIRESTORE_PORT || 8080),
  storage: Number(process.env.REACT_APP_EMULATOR_STORAGE_PORT || 9199),
  functions: Number(process.env.REACT_APP_EMULATOR_FUNCTIONS_PORT || 5001),
};

if (shouldUseEmulator) {
  console.log("EMULATOR running");
  try {
    connectAuthEmulator(firebaseAuth, `http://localhost:${emulatorPorts.auth}`, {
      disableWarnings: true,
    });
    connectFirestoreEmulator(firebaseDB, "localhost", emulatorPorts.firestore);
    connectStorageEmulator(firebaseStorage, "localhost", emulatorPorts.storage);
    connectFunctionsEmulator(firebaseFunctions, "localhost", emulatorPorts.functions);
  } catch (error) {
    // Emulators might already be connected (e.g., in hot reload scenarios)
    if (error.message && error.message.includes("already")) {
      // Silently ignore - emulators already connected
    } else {
      console.error("❌ Error connecting to emulators:", error);
    }
  }
}

/* Constant */
export const COLLECTIONS = {
  Users: "Users",
  UserLibrary: "UserLibrary",
  Subscriptions: "Subscriptions",
  Invites: "Invites",
  Programmings: "ProgrammingsProducts",
  WorkoutCards: "WorkoutCards",
  WorkoutResults: "WorkoutResults",
  Records: "Records",
  CoachingSubscriptions: PRODUCT.TYPE.CoachingSubscription,
  Memberships: PRODUCT.TYPE.Membership,
  // Box collections (NEW)
  Boxes: "Boxes",
  BoxMembers: "Members",
  BoxMemberships: "Memberships", // Subcollection under Boxes
  BoxPrograms: "Programs",
  BoxInvitations: "Invitations",
  BoxSettings: "Settings",
  LandingPage: "LandingPage", // Top-level: the public storefront pages
  // Class booking system collections (NEW)
  BoxClassTypes: "ClassTypes", // Subcollection under Boxes
  BoxClasses: "Classes", // Subcollection under Boxes
  BoxClassBookings: "ClassBookings", // Subcollection under Boxes
  BoxClassWaitlist: "ClassWaitlist", // Subcollection under Boxes
  UserSettings: "Settings", // Subcollection under Users — Users/{uid}/Settings/preferences
  // Notification preferences
  NotificationPreferences: "NotificationPreferences", // Subcollection under Users
  AccessNotices: "AccessNotices", // Subcollection under Users; written only by the grant path
};

/* References to collections */
export const usersCollection = collection(firebaseDB, COLLECTIONS.Users);
export const workoutCollection = collection(firebaseDB, COLLECTIONS.WorkoutCards);
